import { tz } from '@date-fns/tz'
import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  format,
} from 'date-fns'
import { ja as jaLocale } from 'date-fns/locale'

// NOTE: WEAR内のすべての日付は、言語設定、タイムゾーンに関わらず日本時間で表示する
export const makeTokyoTz = tz('Asia/Tokyo')

/**
 * yyyy/MM/dd形式にフォーマットする
 * ex: 2022/03/09
 * (ja=true)yyyy年MM月dd日形式にフォーマットする
 * ex: 2022年03月09日
 */
export const dateFormatToYYYYMMDD = (dateTime: Date | string, ja = false) => {
  const date = makeTokyoTz(dateTime)
  return format(date, ja ? 'yyyy年MM月dd日' : 'yyyy/MM/dd')
}

/**
 * yyyy/M/d E形式にフォーマットする
 * ex: 2022/3/30 Wed
 * (ja=true)yyyy/M/d (E)形式にフォーマットする
 * ex: 2022/3/30 (水)
 */
export const dateFormatToYMDE = (
  dateTime: Date | string,
  ja = false,
  isNotSlash = false
) => {
  const date = makeTokyoTz(dateTime)
  return format(
    date,
    ja ? (isNotSlash ? 'yyyy年M月d日 (E)' : 'yyyy/M/d (E)') : 'yyyy/M/d E',
    ja ? { locale: jaLocale } : {}
  )
}

/**
 * M/d E形式にフォーマットする
 * ex: 3/30 Wed
 * (ja=true)M/d (E)形式にフォーマットする
 * ex: 3/30 (水)
 */
export const dateFormatToMDE = (dateTime: Date | string, ja = false) => {
  const date = makeTokyoTz(dateTime)
  return format(date, ja ? 'M/d (E)' : 'M/d E', ja ? { locale: jaLocale } : {})
}

/**
 * M/d形式にフォーマットする
 * ex: 3/30
 * (ja=true)M月d日形式にフォーマットする
 * ex: 3月30日
 */
export const dateFormatToMD = (dateTime: Date | string, ja = false) => {
  const date = makeTokyoTz(dateTime)
  return format(date, ja ? 'M月d日' : 'M/d')
}

/**
 * H:mm形式にフォーマットする
 * ex: 10:01
 */
export const dateFormatToTime = (dateTime: Date | string) => {
  const date = makeTokyoTz(dateTime)
  return format(date, 'H:mm')
}

/**
 * M/d H:mm形式にフォーマットする
 * ex: 3/30 23:55
 * (ja=true)M月d日H時mm分形式にフォーマットする
 * ex: 3月30日23時55分
 */
export const dateFormatToMDT = (dateTime: Date | string, ja = false) => {
  const date = makeTokyoTz(dateTime)
  return format(date, ja ? 'M月d日H時mm分' : 'M/d H:mm')
}

/**
 * Y/M/d H:mm形式にフォーマットする
 * ex: 2022/02/02 23:55
 * (ja=true)M月d日H時mm分形式にフォーマットする
 * ex: 2022年02月02日23時55分
 */
export const dateFormatToYYYYMMDDT = (dateTime: Date | string, ja = false) => {
  const date = makeTokyoTz(dateTime)
  return format(date, ja ? 'yyyy年MM月dd日H時mm分' : 'yyyy/MM/dd H:mm')
}

/**
 * 任意の形式にフォーマットする
 */
export const dateFormat = (dateTime: Date | string, formatString: string) => {
  const date = makeTokyoTz(dateTime)
  return format(date, formatString)
}

/**
 * 現在時刻との差分形式でフォーマットする
 */
export const dateFormatDelta = (dateTime: Date | string): string => {
  const now = makeTokyoTz(new Date()).valueOf() /* ms */
  const target = makeTokyoTz(dateTime).valueOf() /* ms */
  const diffMins = Math.floor((now - target) / 1000 / 60)
  if (diffMins < 1) return 'たった今'
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 1) return `${diffMins}分前`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 1) return `${diffHours}時間前`
  const diffWeeks = Math.floor(diffDays / 7)
  if (diffWeeks < 1) return `${diffDays}日前`
  const diff4Weeks = Math.floor(diffWeeks / 4)
  if (diff4Weeks < 1) return `${diffWeeks}週間前`
  const diffMonths = differenceInCalendarMonths(now, target)
  const diffYears = Math.floor(diffMonths / 12)
  if (diffYears < 1) return `${diffMonths}ヶ月前`
  return `${diffYears}年前`
}

/**
 * 1週間以内なら差分形式でフォーマット
 * 1週間以上ならyyyy/M/d形式でフォーマット
 */
export const dateFormatDeltaOrYMD = (dateTime: Date | string) => {
  return differenceInCalendarDays(
    makeTokyoTz(new Date()),
    makeTokyoTz(dateTime)
  ) < 7
    ? dateFormatDelta(dateTime)
    : dateFormatToYYYYMMDD(dateTime)
}

/**
 * 春夏秋冬を返す
 */
export const getSeasonFromDate = (
  dateTime: Date | string
): '春' | '夏' | '秋' | '冬' => {
  const date = makeTokyoTz(dateTime)
  const month = date.getMonth() + 1

  /* eslint-disable yoda */
  if (3 <= month && month <= 5) return '春'
  if (6 <= month && month <= 8) return '夏'
  if (9 <= month && month <= 11) return '秋'
  /* eslint-enable yoda */
  return '冬'
}
